import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { CheckCircle, Lock, PenLine, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { evaluationsAPI } from '../lib/api';
import { SignatureNotice } from './SignatureNotice';

/**
 * Part2Panel — the 6-Month Re-Review block that lives on a completed 90-Day
 * Trial evaluation. Part 1 stays locked above; this panel only touches the
 * `part2` sub-doc on the same record via /evaluations/{id}/part2/*.
 *
 * Behavior mirrors the spec:
 *  - Not opened yet -> show a single "Open 6-Month Re-Review" button.
 *  - Once opened, ask Standards Confirmation (YES/NO) + comment fields.
 *  - NO -> require sections_not_met + action_taken + HR signature.
 *  - Signatures: employee / supervisor / reviewer always; HR only when NO.
 */
const SECTION_LABELS = {
  A: 'Professionalism',
  B: 'Safety & Security',
  C: 'Customer Service',
  D: 'Uniform & Appearance',
  E: 'Understanding of Requirements',
  F: 'Core Values Demonstration',
};

export function Part2Panel({ ev, onChanged }) {
  const p2 = ev.part2;
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const [savedTick, setSavedTick] = useState(0);
  const [highlightSigs, setHighlightSigs] = useState(false);
  const [draft, setDraft] = useState({
    standards_still_met: p2?.standards_still_met ?? null,
    sections_not_met: p2?.sections_not_met ?? [],
    progress_since_90_day: p2?.progress_since_90_day ?? '',
    areas_still_developing: p2?.areas_still_developing ?? '',
    supervisor_comments: p2?.supervisor_comments ?? '',
    employee_comments: p2?.employee_comments ?? '',
    action_taken: p2?.action_taken ?? '',
  });

  const openPart2 = async () => {
    setBusy(true);
    try {
      await evaluationsAPI.openPart2(ev.id);
      toast.success('6-Month Review opened. The 90-Day evaluation is now locked.');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the 6-Month Review');
    } finally { setBusy(false); }
  };

  const savePart2 = async () => {
    setBusy(true);
    try {
      await evaluationsAPI.updatePart2(ev.id, draft);
      toast.success('6-Month Review saved');
      onChanged?.();
      setSavedTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save the 6-Month Review');
    } finally { setBusy(false); }
  };

  // After a save, keep the user in place: scroll to the signatures block and
  // flash a highlight so the next step (signing) is right there.
  useEffect(() => {
    if (!savedTick) return;
    const t = setTimeout(() => {
      sigRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightSigs(true);
      setTimeout(() => setHighlightSigs(false), 1800);
    }, 150);
    return () => clearTimeout(t);
  }, [savedTick]);

  const signPart2 = async (role) => {
    const name = prompt(`Type your name to sign as ${role.toUpperCase()}:`);
    if (!name) return;
    setBusy(true);
    try {
      await evaluationsAPI.signPart2(ev.id, { role, signature: name });
      toast.success(`Signed as ${role}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    } finally { setBusy(false); }
  };

  const sendEmployeeLink = async () => {
    setBusy(true);
    try {
      const r = await evaluationsAPI.sendPart2EmployeeLink(ev.id);
      toast.success(r.data?.message || 'Signing link sent to the employee');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the signing link');
    } finally { setBusy(false); }
  };

  const recordRefusal = async () => {
    if (!window.confirm('Record that the employee refused to sign? This completes the employee row without their signature.')) return;
    setBusy(true);
    try {
      await evaluationsAPI.signPart2(ev.id, { role: 'employee', refused_to_sign: true, signature: 'Refused to sign' });
      toast.success('Refusal to sign recorded');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record refusal');
    } finally { setBusy(false); }
  };

  const toggleSection = (letter) => {
    setDraft(d => ({
      ...d,
      sections_not_met: d.sections_not_met.includes(letter)
        ? d.sections_not_met.filter(s => s !== letter)
        : [...d.sections_not_met, letter],
    }));
  };

  const hrRequired = draft.standards_still_met === false;
  const sigs = p2?.signatures || [];
  const signedRoles = new Set(sigs.map(s => s.role));
  const employeeSig = sigs.find(s => s.role === 'employee');
  const employeeRefused = !!employeeSig?.refused_to_sign;
  const internalRoles = hrRequired ? ['supervisor', 'reviewer', 'hr'] : ['supervisor', 'reviewer'];
  const internalComplete = internalRoles.every(r => signedRoles.has(r));
  // Staged signing order: supervisor -> reviewer -> (HR on a NO). The next
  // signer's button stays locked until the prior role has signed.
  const signOrderBlocked = (role) => {
    if (role === 'reviewer') return !signedRoles.has('supervisor');
    if (role === 'hr') return !(signedRoles.has('supervisor') && signedRoles.has('reviewer'));
    return false;
  };
  const requiredRoles = hrRequired
    ? ['employee', 'supervisor', 'reviewer', 'hr']
    : ['employee', 'supervisor', 'reviewer'];
  const allSigned = requiredRoles.every(r => signedRoles.has(r));
  const noEmail = p2?.employee_link_error === 'no_email';
  const awaitingEmployee = internalComplete && !signedRoles.has('employee');

  return (
    <Card data-testid="part2-panel" className="border-blue-200">
      <CardHeader className="bg-blue-50/60">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            6-Month Review
          </CardTitle>
          {ev.part1_locked_at && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> 90-Day locked
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {!p2 ? (
          <div className="text-center py-6">
            <p className="text-slate-600 mb-3">
              The 90-Day evaluation is complete. Open the 6-Month Review to begin.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              6-Month Due (calculated from hire date): <strong>
                {(() => {
                  const h = ev.hire_date_snapshot || ev.employee_snapshot?.hire_date;
                  if (!h) return '—';
                  try { const d = new Date(h); d.setDate(d.getDate() + 180); return d.toISOString().split('T')[0]; }
                  catch { return '—'; }
                })()}
              </strong>
            </p>
            <Button onClick={openPart2} disabled={busy} data-testid="open-part2-btn">
              Open 6-Month Review
            </Button>
          </div>
        ) : (
          <>
            <div>
              <div className="text-sm text-slate-500 mb-1">6-Month Due Date</div>
              <div className="font-medium text-slate-900" data-testid="part2-due">{p2.six_month_due_date || '—'}</div>
            </div>

            {/* Standards Confirmation */}
            <div className="space-y-2">
              <Label>Is the employee still meeting the standards recorded in the 90-day evaluation? *</Label>
              <div className="flex gap-2">
                <Button
                  variant={draft.standards_still_met === true ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDraft(d => ({ ...d, standards_still_met: true }))}
                  data-testid="part2-standards-yes"
                >Yes</Button>
                <Button
                  variant={draft.standards_still_met === false ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDraft(d => ({ ...d, standards_still_met: false }))}
                  data-testid="part2-standards-no"
                  className={draft.standards_still_met === false ? 'bg-red-600 hover:bg-red-700' : ''}
                >No</Button>
              </div>
            </div>

            {/* Sections not met — only when NO */}
            {draft.standards_still_met === false && (
              <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
                <Label className="text-red-900">Which sections are no longer being met? *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(SECTION_LABELS).map(([letter, label]) => (
                    <label key={letter} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={draft.sections_not_met.includes(letter)}
                        onCheckedChange={() => toggleSection(letter)}
                        data-testid={`part2-section-${letter}`}
                      />
                      <span><strong>{letter}</strong> — {label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Progress since the 90-day evaluation</Label>
              <Textarea rows={3} value={draft.progress_since_90_day} onChange={e => setDraft(d => ({ ...d, progress_since_90_day: e.target.value }))} data-testid="part2-progress" />
            </div>
            <div className="space-y-1">
              <Label>Areas still requiring development</Label>
              <Textarea rows={3} value={draft.areas_still_developing} onChange={e => setDraft(d => ({ ...d, areas_still_developing: e.target.value }))} data-testid="part2-areas" />
            </div>
            <div className="space-y-1">
              <Label>Supervisor comments</Label>
              <Textarea rows={3} value={draft.supervisor_comments} onChange={e => setDraft(d => ({ ...d, supervisor_comments: e.target.value }))} data-testid="part2-sup-comments" />
            </div>
            <div className="space-y-1">
              <Label>Employee comments</Label>
              <Textarea rows={3} value={draft.employee_comments} onChange={e => setDraft(d => ({ ...d, employee_comments: e.target.value }))} data-testid="part2-emp-comments" />
            </div>
            {draft.standards_still_met === false && (
              <div className="space-y-1">
                <Label>Action taken *</Label>
                <Textarea rows={3} value={draft.action_taken} onChange={e => setDraft(d => ({ ...d, action_taken: e.target.value }))} data-testid="part2-action-taken" />
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={savePart2} disabled={busy} data-testid="part2-save-btn">
                {busy ? 'Saving…' : 'Save 6-Month Review'}
              </Button>
            </div>

            {/* Signatures */}
            <div ref={sigRef} data-testid="part2-signatures-block"
                 className={`border-t pt-4 rounded-md transition-all duration-500 ${highlightSigs ? 'ring-2 ring-blue-500 ring-offset-2 bg-blue-50/50' : ''}`}>
              <h4 className="font-semibold text-slate-900 mb-2">Signatures</h4>
              <SignatureNotice hrEmail={ev.hr_email} className="mb-3" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Internal signers — signed in person by the logged-in reviewer/supervisor/HR */}
                {internalRoles.map(role => (
                  <div key={role} className="flex items-center justify-between border border-slate-200 rounded p-2">
                    <div>
                      <div className="text-xs text-slate-500 uppercase">{role}</div>
                      {signedRoles.has(role) ? (
                        <div className="text-sm text-emerald-700 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> Signed
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400">Not signed</div>
                      )}
                    </div>
                    {!signedRoles.has(role) && draft.standards_still_met !== null && (
                      signOrderBlocked(role) ? (
                        <div className="text-xs text-slate-400 text-right" data-testid={`part2-sign-${role}-blocked`}>
                          {role === 'reviewer' ? 'After supervisor signs' : 'After supervisor & reviewer'}
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => signPart2(role)} disabled={busy} data-testid={`part2-sign-${role}`}>
                          <PenLine className="w-3.5 h-3.5 mr-1" /> Sign
                        </Button>
                      )
                    )}
                  </div>
                ))}
                {/* Employee — always signs last, via the secure emailed link */}
                <div className="flex items-center justify-between border border-slate-200 rounded p-2 md:col-span-2" data-testid="part2-employee-row">
                  <div>
                    <div className="text-xs text-slate-500 uppercase">employee</div>
                    {employeeRefused ? (
                      <div className="text-sm text-amber-700 flex items-center gap-1" data-testid="part2-employee-refused">
                        Refused to sign
                      </div>
                    ) : signedRoles.has('employee') ? (
                      <div className="text-sm text-emerald-700 flex items-center gap-1" data-testid="part2-employee-signed">
                        <CheckCircle className="w-3.5 h-3.5" /> Signed
                      </div>
                    ) : !internalComplete ? (
                      <div className="text-sm text-slate-400" data-testid="part2-employee-waiting-internal">
                        Signs last — capture the internal signatures first
                      </div>
                    ) : (
                      <div className="text-sm text-blue-700 flex items-center gap-1" data-testid="part2-employee-awaiting">
                        <Mail className="w-3.5 h-3.5" /> Awaiting employee
                      </div>
                    )}
                  </div>
                  {awaitingEmployee && !employeeRefused && (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex gap-2">
                        <Button size="sm" onClick={sendEmployeeLink} disabled={busy} data-testid="part2-send-employee-link">
                          <Mail className="w-3.5 h-3.5 mr-1" />
                          {p2?.employee_link_sent_at ? 'Resend signing link' : 'Send signing link'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={recordRefusal} disabled={busy} data-testid="part2-record-refusal">
                          Record refusal to sign
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {awaitingEmployee && noEmail && (
                <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="part2-no-email-warning">
                  No email on file for this employee. Add an email to the employee record, then send the signing link.
                </div>
              )}
              {allSigned && (
                <div className="mt-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                  ✓ 6-Month Review complete — all required signatures captured.
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
